import React, { useEffect } from "react";
// import DropdownMessage from "./DropdownMessage";
import ApiService from "@/Services/ApiService";
import DropdownNotification from "./DropdownNotification";
import DropdownUser from "./DropdownUser";
import DarkModeSwitcher from "./DarkModeSwitcher";
import { Link, usePage } from "@inertiajs/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faGlobe,
    faHouse,
    faPlus,
    faVideo,
    faBroadcastTower,
    faHeart,
} from "@fortawesome/free-solid-svg-icons";
import { faBuilding } from "@fortawesome/free-solid-svg-icons";
import { faNewspaper } from "@fortawesome/free-solid-svg-icons";
import {
    faFilm,
    faGraduationCap,
} from "@fortawesome/free-solid-svg-icons";
import Logo from "../../images/logo/logo.svg";
import LogoDark from "../../images/logo/logo-dark.svg";
import MessagesNavLink from "@/Components/Messaging/MessagesNavLink";
import { getOrganizationsLabels } from "@/utils/organizationsLabel";

const Header = (props) => {
    const { props: usePageProps, url } = usePage();

    const {
        globalSettings,
        buckets,
        ga4MeasurementId,
        adsensePublisherKey,
        tenantSlug,
    } = usePageProps;

    // Extract user and subscriptions
    const user = usePageProps?.auth?.user;

    const hasActiveSubscription =
        Array.isArray(user?.stripe_subscriptions) &&
        user.stripe_subscriptions.some((sub) => sub.status === "active");

    const canAccessAdmin =
        (Array.isArray(user?.roles) &&
            user.roles.some((role) =>
                ["Admin", "Super Admin"].includes(role.role_name),
            )) ||
        (Array.isArray(user?.permissions) &&
            user.permissions.some((permission) =>
                String(
                    permission?.permission_name ?? permission ?? "",
                ).includes("view_admin_dashboard"),
            ));

    // Check if user has go_live permission
    const hasGoLivePermission = user?.permissions?.some(
        (permission) =>
            (permission?.permission_name ?? permission) === "go_live",
    );

    // Check if user has view_live permission
    const hasViewLivePermission = user?.permissions?.some(
        (permission) =>
            (permission?.permission_name ?? permission) === "view_live",
    );

    const staticAssetsBucket = buckets?.static_assets?.name ?? "";
    const profileImagesRegion = buckets?.profile_images?.region ?? "";

    const logoBaseUrl = `https://${staticAssetsBucket}.s3.${profileImagesRegion}.amazonaws.com/${tenantSlug}/`;
    const { plural: organizationsLabel, singular: organizationLabel } =
        getOrganizationsLabels(globalSettings);
    const videosLabel = globalSettings?.videos_label?.trim() || "Videos";

    useEffect(() => {
        const analyticsUrl = route("g4-google-analytics.store_client_id");

        if (ga4MeasurementId && typeof gtag === "function") {
            gtag("get", ga4MeasurementId, "client_id", (clientId) => {
                ApiService.post(analyticsUrl, {
                    client_id: clientId,
                });
            });
        }
    }, [ga4MeasurementId]);

    useEffect(() => {
        if (typeof document === "undefined") return;
        if (!adsensePublisherKey || hasActiveSubscription) return;

        // Check if script already exists to avoid duplicates
        const existing = document.querySelector(
            `script[src*="adsbygoogle.js?client=${adsensePublisherKey}"]`,
        );
        if (existing) return;

        const script = document.createElement("script");
        script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${adsensePublisherKey}`;
        script.async = true;
        script.crossOrigin = "anonymous";
        document.head.appendChild(script);
    }, [adsensePublisherKey, hasActiveSubscription]);

    const onAdminPath = url?.startsWith("/admin");
    const showAdminSidebarToggle =
        Boolean(props.setSidebarOpen) && canAccessAdmin && onAdminPath;

    return (
        <header className="sticky top-0 z-999 flex w-full bg-white drop-shadow-1 dark:bg-boxdark dark:drop-shadow-none">
            <div className="flex flex-grow items-center justify-between px-4 py-4 shadow-2 md:px-6 2xl:px-11">
                <div className="flex items-center gap-2 sm:gap-4 lg:hidden">
                    {/* <!-- Hamburger Toggle BTN for Admin --> */}
                    {showAdminSidebarToggle && (
                        <button
                            type="button"
                            aria-controls="sidebar"
                            aria-expanded={Boolean(props.sidebarOpen)}
                            aria-label={
                                props.sidebarOpen
                                    ? "Close admin menu"
                                    : "Open admin menu"
                            }
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                props.setSidebarOpen(!props.sidebarOpen);
                            }}
                            className="z-99999 block rounded-sm border border-stroke bg-white p-1.5 shadow-sm dark:border-strokedark dark:bg-boxdark lg:hidden"
                        >
                            <span className="relative block h-5.5 w-5.5 cursor-pointer">
                                <span className="du-block absolute right-0 h-full w-full">
                                    <span
                                        className={`relative left-0 top-0 my-1 block h-0.5 w-0 rounded-sm bg-black delay-[0] duration-200 ease-in-out dark:bg-white ${
                                            !props.sidebarOpen &&
                                            "!w-full delay-300"
                                        }`}
                                    ></span>
                                    <span
                                        className={`relative left-0 top-0 my-1 block h-0.5 w-0 rounded-sm bg-black delay-150 duration-200 ease-in-out dark:bg-white ${
                                            !props.sidebarOpen &&
                                            "delay-400 !w-full"
                                        }`}
                                    ></span>
                                    <span
                                        className={`relative left-0 top-0 my-1 block h-0.5 w-0 rounded-sm bg-black delay-200 duration-200 ease-in-out dark:bg-white ${
                                            !props.sidebarOpen &&
                                            "!w-full delay-500"
                                        }`}
                                    ></span>
                                </span>
                                <span className="absolute right-0 h-full w-full rotate-45">
                                    <span
                                        className={`absolute left-2.5 top-0 block h-full w-0.5 rounded-sm bg-black delay-300 duration-200 ease-in-out dark:bg-white ${
                                            !props.sidebarOpen &&
                                            "!h-0 !delay-[0]"
                                        }`}
                                    ></span>
                                    <span
                                        className={`delay-400 absolute left-0 top-2.5 block h-0.5 w-full rounded-sm bg-black duration-200 ease-in-out dark:bg-white ${
                                            !props.sidebarOpen &&
                                            "!h-0 !delay-200"
                                        }`}
                                    ></span>
                                </span>
                            </span>
                        </button>
                    )}

                    <Link className="block flex-shrink-0 lg:hidden" href="/">
                        <div className=" inline-block">
                            <img
                                className="hidden dark:block w-24 sm:w-32 md:w-40 max-w-40 max-h-12 object-contain"
                                src={
                                    globalSettings?.logo_dark_file_name
                                        ? logoBaseUrl +
                                          globalSettings.logo_dark_file_name
                                        : Logo
                                }
                                alt="Logo"
                            />
                            <div className="flex items-center space-x-4">
                                <img
                                    className="dark:hidden w-30 sm:w-32 md:w-40 max-w-40 max-h-12 object-contain"
                                    src={
                                        globalSettings?.logo_file_name
                                            ? logoBaseUrl +
                                              globalSettings.logo_file_name
                                            : LogoDark
                                    }
                                    alt="Logo"
                                />
                                {/* <h2 className="text-2xl font-bold text-black dark:text-white sm:text-title-xl2">
                                        {appName}
                                    </h2> */}
                            </div>
                        </div>
                    </Link>
                </div>

                <div className="hidden lg:block flex-shrink-0">
                    <a
                        href={"/"}
                        className="flex items-center gap-1.5 text-sm font-medium duration-300 ease-in-out hover:text-primary lg:text-base"
                    >
                        <div className="inline-block">
                            <img
                                className="hidden dark:block w-40 max-w-40 max-h-12 object-contain"
                                src={
                                    globalSettings?.logo_dark_file_name
                                        ? logoBaseUrl +
                                          globalSettings.logo_dark_file_name
                                        : Logo
                                }
                                alt="Logo"
                            />
                            <img
                                className="dark:hidden w-40 max-w-40 max-h-12 object-contain"
                                src={
                                    globalSettings?.logo_file_name
                                        ? logoBaseUrl +
                                          globalSettings.logo_file_name
                                        : LogoDark
                                }
                                alt="Logo"
                            />
                        </div>
                    </a>
                </div>

                <div className="hidden lg:flex items-center gap-4 flex-grow justify-center">
                    <a
                        href={route("users.posts.index")}
                        className="flex items-center gap-1.5 text-sm font-medium duration-300 ease-in-out hover:text-primary lg:text-base"
                    >
                        <FontAwesomeIcon
                            icon={faHouse}
                            className="cursor-pointer"
                        />
                        Home
                    </a>
                    {/* <Link
                        href={route("users.posts.index")}
                        className="flex items-center gap-1.5 text-sm font-medium duration-300 ease-in-out hover:text-primary lg:text-base"
                    >
                        <FontAwesomeIcon
                            icon={faHouse}
                            className="cursor-pointer"
                        />
                        Home
                    </Link> */}

                    {/* Donate button, only if donation_url exists */}
                    {globalSettings?.donation_url && (
                        <a
                            href={globalSettings.donation_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1.5 text-sm font-medium duration-300 ease-in-out hover:text-primary lg:text-base"
                            style={{ color: "#0077B6" }}
                        >
                            <FontAwesomeIcon
                                icon={faHeart}
                                className="cursor-pointer"
                                style={{ color: "#0077B6" }}
                            />
                            Give
                        </a>
                    )}

                    {globalSettings?.organizations_service_enabled !==
                        false && (
                        <Link
                            href={route("user.organizations.index")}
                            className="flex items-center gap-1.5 text-sm font-medium duration-300 ease-in-out hover:text-primary lg:text-base"
                        >
                            <FontAwesomeIcon
                                icon={faBuilding}
                                className="cursor-pointer"
                            />
                            {organizationsLabel}
                        </Link>
                    )}

                    <Link
                        href={route("users.blogs.index")}
                        className="flex items-center gap-1.5 text-sm font-medium duration-300 ease-in-out hover:text-primary lg:text-base"
                    >
                        <FontAwesomeIcon
                            icon={faNewspaper}
                            className="cursor-pointer"
                        />
                        Blog
                    </Link>

                    {/* Courses Link */}
                    {globalSettings?.courses_service_enabled && (
                    <Link
                        href={route("courses.index")}
                        className="flex items-center gap-1.5 text-sm font-medium duration-300 ease-in-out hover:text-primary lg:text-base"
                    >
                        <FontAwesomeIcon
                            icon={faGraduationCap}
                            className="cursor-pointer"
                        />
                        Courses
                    </Link>
                    )}

                    {user && <MessagesNavLink />}

                    {/* About Us link, only if About Us Page URL exists */}

                    {user && globalSettings?.vod_service_enabled && (
                        <Link
                            href={route("videos.index")}
                            className="flex items-center gap-1.5 text-sm font-medium duration-300 ease-in-out hover:text-primary lg:text-base"
                        >
                            <FontAwesomeIcon
                                icon={faFilm}
                                className="cursor-pointer"
                            />
                            {videosLabel}
                        </Link>
                    )}

                    {/* Live Streaming Links - only show if user has go_live permission */}
                    {user &&
                        hasGoLivePermission &&
                        globalSettings?.live_streaming_enabled && (
                            <Link
                                href={route("live.start")}
                                className="flex items-center gap-1.5 text-sm font-medium duration-300 ease-in-out text-red  hover:text-gray-600 dark:hover:text-gray-400 lg:text-base animate-pulse"
                            >
                                <FontAwesomeIcon
                                    icon={faBroadcastTower}
                                    className="cursor-pointer"
                                />
                                Go Live
                            </Link>
                        )}

                    {/* View Live Link - only show if user has view_live permission */}
                    {user &&
                        hasViewLivePermission &&
                        globalSettings?.live_streaming_enabled && (
                            <Link
                                href={route("live.view")}
                                className="flex items-center gap-1.5 text-sm font-medium duration-300 ease-in-out hover:text-primary lg:text-base"
                            >
                                <FontAwesomeIcon
                                    icon={faVideo}
                                    className="cursor-pointer"
                                />
                                LIVE
                            </Link>
                        )}

                    {/* About Us link, only if About Us Page URL exists */}
                    {globalSettings?.about_us_page_url && (
                        <a
                            href={globalSettings.about_us_page_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1.5 text-sm font-medium duration-300 ease-in-out hover:text-primary lg:text-base"
                        >
                            <FontAwesomeIcon
                                icon={faGlobe}
                                className="cursor-pointer"
                            />
                            About Us
                        </a>
                    )}
                    {/* {user && (
                        <Link
                            href={route("user.organizations.create")}
                            className="flex items-center gap-1.5 text-sm font-medium duration-300 ease-in-out hover:text-primary lg:text-base"
                        >
                            <FontAwesomeIcon
                                icon={faPlus}
                                className="cursor-pointer"
                            />
                            Add {organizationLabel}
                        </Link>
                    )} */}
                </div>

                <div className="flex items-center gap-3 2xsm:gap-7">
                    <ul className="flex items-center gap-2 2xsm:gap-4">
                        {/* <!-- Dark Mode Toggler --> */}
                        <DarkModeSwitcher />
                        {/* <!-- Dark Mode Toggler --> */}

                        {/* <!-- Notification Menu Area --> */}
                        <DropdownNotification />
                        {/* <!-- Notification Menu Area --> */}

                        {/* Messages unread badge */}
                        <li className="relative list-none">
                            <MessagesNavLink compact />
                        </li>
                    </ul>

                    {/* <!-- User Area --> */}
                    <DropdownUser />
                    {/* <!-- User Area --> */}
                </div>
            </div>
        </header>
    );
};

export default Header;
